import React from "react";

import classNames from "classnames";
import Slider from "rc-slider";
import "rc-slider/assets/index.css";
import { useTranslation } from "react-i18next";
import MarkdownRenderer from "react-markdown-renderer";

import ImageFormatSelector from "./ImageFormatSelector";
import WidgetConfig from "./section/WidgetConfig";
import SwitchOnOff from "../../../components/common/tools/SwitchOnOff";
import ComponentDisplayOrder from "../../../components/platform/layout/ComponentDisplayOrder";
import CarouselButtonPosition from "../../../components/utils/CarouselButtonPosition";
import InfoTooltip from "../../../components/utils/InfoTooltip";
import { COMPONENT_TYPES } from "../../../constants/componentType";
import { DEFAULT_IMAGE_FORMATS } from "../../../constants/ratio";
import { getComponentByType } from "../../../utils/data/Architecture";
import {
  isCarouselComponent,
  isMosaicComponent,
  isMosaicWithIndividualCarouselComponent,
} from "../../../utils/typePredicate";
import { editor_primary_color } from "../../../utils/Utilities";

import type { JSX } from "react";

import type {
  CarouselComponent,
  CarouselParams,
  DisplayOrder,
  ImageFormatParams,
  MosaicComponent,
  MosaicParams,
  OptsIndividualCarousel,
  PlatformComponent,
  WidgetComponent,
  WidgetParams,
} from "../../../api/ProjectAPI.types";
import type { ImageFormat } from "../../../constants/ratio";

const { WIDGET, CAROUSEL, MOSAIC } = COMPONENT_TYPES;

const INDIVIDUAL_CAROUSEL_PARAM_KEYS = [
  "arrows_on_hover",
  "circular",
  "component_per_slides",
  "display_arrows",
];

const COMPONENT_LABELS: Record<
  "smartlist" | "collection",
  {
    showComponentTitle: string;
    carouselIndividualInfo: string;
    mosaicIndividualInfo: string;
  }
> = {
  smartlist: {
    showComponentTitle: "showComponentTitle",
    carouselIndividualInfo: "componentTypeCarouselSmartlistInfo",
    mosaicIndividualInfo: "componentTypeMosaicSmartlistInfo",
  },
  collection: {
    showComponentTitle: "showComponentTitleCollection",
    carouselIndividualInfo: "componentTypeCarouselCollectionInfo",
    mosaicIndividualInfo: "componentTypeMosaicCollectionInfo",
  },
};

interface CommonTypeEditorProps {
  allowWidget?: boolean;
  children?: React.ReactNode;
  onChangeComponents: (components: PlatformComponent[]) => void;
  components: PlatformComponent[];
  pageType: string;
  imageFormatList?: ImageFormat[];
}

function CommonTypeEditor({
  allowWidget,
  children,
  onChangeComponents,
  components,
  pageType,
  imageFormatList = DEFAULT_IMAGE_FORMATS,
}: CommonTypeEditorProps): JSX.Element | null {
  const { t } = useTranslation("editor-platform");

  const carouselOrMosaicComponent:
    | CarouselComponent
    | MosaicComponent
    | undefined =
    getComponentByType(components, MOSAIC) ??
    getComponentByType(components, CAROUSEL);
  const widgetComponent: WidgetComponent = getComponentByType(
    components,
    WIDGET,
  );
  const componentTypeWithDefaultTitle = ["casting", "playlists"];
  const showTitle = !componentTypeWithDefaultTitle.includes(pageType);

  const isSmartlistOrCollectionPage =
    pageType === "smartlist" || pageType === "collection";

  const componentLabels =
    COMPONENT_LABELS[pageType === "collection" ? "collection" : "smartlist"];

  if (!carouselOrMosaicComponent) return null;

  const handleChangeWidgetComponentEnabled = (value: boolean): void => {
    const updatedComponents = components.map((component) => {
      if (component.type === WIDGET) {
        return {
          ...component,
          enabled: value,
        };
      }
      return component;
    });
    onChangeComponents(updatedComponents);
  };

  const handleChangeWidgetComponent = (
    value: WidgetParams[keyof WidgetParams],
    opt: keyof WidgetParams,
  ): void => {
    const updatedComponents = components.map((component) => {
      if (component.type === WIDGET) {
        return {
          ...component,
          params: {
            ...component.params,
            [opt]: value,
          },
        };
      }
      return component;
    });
    onChangeComponents(updatedComponents);
  };

  const handleChangeDisplayButton = (value: boolean): void => {
    const updatedComponents = components.map((component) => {
      if (isCarouselComponent(component)) {
        return {
          ...component,
          params: {
            ...component.params,
            display_button: value,
            button_horizontal_position:
              component.params?.button_horizontal_position ?? "left",
            button_vertical_position:
              component.params?.button_vertical_position ?? "bottom",
          },
        };
      }
      return component;
    });
    onChangeComponents(updatedComponents);
  };

  const handleChangeIndividualCarousel = (value: boolean): void => {
    const updatedComponents = components.map((component) => {
      if (isMosaicComponent(component) || isCarouselComponent(component)) {
        if (value) {
          return {
            ...component,
            params: {
              ...component.params,
              individual_carousel: true,
              opts_individual_carousel: {
                arrows_on_hover:
                  component.params.opts_individual_carousel?.arrows_on_hover ??
                  true,
                circular:
                  component.params.opts_individual_carousel?.circular ?? true,
                component_per_slides:
                  component.params.opts_individual_carousel
                    ?.component_per_slides ?? 6,
                display_arrows:
                  component.params.opts_individual_carousel?.display_arrows ??
                  true,
              },
            },
          };
        }

        return {
          ...component,
          params: {
            ...component.params,
            individual_carousel: false,
          },
        };
      }
      return component;
    });

    onChangeComponents(updatedComponents as PlatformComponent[]);
  };

  const handleChangeButtonPosition = (
    horizontalPosition: "left" | "center" | "right",
    verticalPosition: "top" | "center" | "bottom",
  ): void => {
    const updatedComponents = components.map((component) => {
      if (isCarouselComponent(component)) {
        return {
          ...component,
          params: {
            ...component.params,
            button_horizontal_position: horizontalPosition,
            button_vertical_position: verticalPosition,
          },
        };
      }
      return component;
    });
    onChangeComponents(updatedComponents);
  };

  const handleChangeOption = (
    option:
      | keyof ImageFormatParams
      | keyof CarouselParams
      | keyof MosaicParams
      | keyof OptsIndividualCarousel
      | keyof WidgetParams
      | "button_horizontal_position"
      | "button_vertical_position",
    value: boolean | number | string | DisplayOrder,
  ): void => {
    const updatedComponents = components.map((component) => {
      if (isMosaicComponent(component) || isCarouselComponent(component)) {
        if (
          INDIVIDUAL_CAROUSEL_PARAM_KEYS.includes(option) &&
          isSmartlistOrCollectionPage
        ) {
          return {
            ...component,
            params: {
              ...component.params,
              ...(isCarouselComponent(component) ? { [option]: value } : {}),
              opts_individual_carousel: {
                ...component.params.opts_individual_carousel,
                [option]: value,
              },
            },
          };
        }

        return {
          ...component,
          params: {
            ...component.params,
            [option]: value,
          },
        };
      }

      return component;
    });

    onChangeComponents(updatedComponents as PlatformComponent[]);
  };

  const handleChangeDisplayOrder = (displayOrder: DisplayOrder): void => {
    handleChangeOption("display_order", displayOrder);
  };

  const handleClickComponentType = (
    type: typeof CAROUSEL | typeof MOSAIC,
  ): void => {
    if (carouselOrMosaicComponent.type === type) return;

    const {
      display_dialog: displayDialog,
      display_item_title: displayItemTitle,
      display_title: displayTitle,
      ratio,
      individual_carousel: individualCarousel,
      opts_individual_carousel: optsIndividualCarousel,
    } = carouselOrMosaicComponent.params;

    const commonParams = {
      individual_carousel: individualCarousel,
      opts_individual_carousel: optsIndividualCarousel,
      display_dialog: displayDialog,
      display_item_title: displayItemTitle,
      display_title: displayTitle,
      ratio,
    };

    const updatedComponents = components.map((component) => {
      if (isMosaicComponent(component) || isCarouselComponent(component)) {
        if (type === MOSAIC) {
          return {
            ...component,
            type,
            params: commonParams,
          };
        }

        if (type === CAROUSEL) {
          return {
            ...component,
            type,
            params: {
              ...commonParams,
              arrows_on_hover:
                component.params.opts_individual_carousel?.arrows_on_hover ??
                true,
              circular:
                component.params.opts_individual_carousel?.circular ?? true,
              component_per_slides:
                component.params.opts_individual_carousel
                  ?.component_per_slides ?? 6,
              display_arrows:
                component.params.opts_individual_carousel?.display_arrows ??
                true,
            },
          };
        }
      }

      return component;
    });

    onChangeComponents(updatedComponents as PlatformComponent[]);
  };

  const isCarousel = isCarouselComponent(carouselOrMosaicComponent);

  const handleChangeComponentPerSlides = (value: number): void => {
    const updatedComponents = components.map((component) => {
      if (isCarouselComponent(component) || isMosaicComponent(component)) {
        const displayButtonParam =
          isCarouselComponent(component) && value !== 1
            ? { display_button: false }
            : {};

        const individualCarouselParams = isSmartlistOrCollectionPage
          ? {
              opts_individual_carousel: {
                ...component.params.opts_individual_carousel,
                component_per_slides: value,
              },
            }
          : {};

        const componentPerSlidesParam = isCarouselComponent(component)
          ? { component_per_slides: value }
          : {};

        return {
          ...component,
          params: {
            ...component.params,
            ...componentPerSlidesParam,
            ...displayButtonParam,
            ...individualCarouselParams,
          },
        };
      }
      return component;
    });
    onChangeComponents(updatedComponents as PlatformComponent[]);
  };

  const isMosaicWithIndividualCarousel =
    isMosaicWithIndividualCarouselComponent(carouselOrMosaicComponent);

  const renderCommonCarouselParams = (
    params: OptsIndividualCarousel | CarouselParams,
  ): JSX.Element => {
    const marks = {
      1: "1",
      2: "2",
      3: "3",
      4: "4",
      5: "5",
      6: "6",
      7: "7",
      8: "8",
      9: "9",
      10: "10",
    };

    return (
      <React.Fragment>
        <div className="editor-form-row editor-form-row-inline">
          <div className="editor-form-input">
            <SwitchOnOff
              checked={params.circular}
              iconName=""
              name="circular"
              size="sm"
              onChange={(value: boolean): void => {
                handleChangeOption("circular", value);
              }}
            />
          </div>
          <div className="editor-form-label">{t("showCircular")}</div>
        </div>
        <div className="editor-form-row editor-form-row-inline">
          <div className="editor-form-input">
            <SwitchOnOff
              checked={params.display_arrows}
              iconName=""
              name="display_arrows"
              size="sm"
              onChange={(value: boolean): void => {
                handleChangeOption("display_arrows", value);
              }}
            />
          </div>
          <div className="editor-form-label">{t("showArrow")}</div>
        </div>
        <div className="editor-form-row editor-form-row-inline">
          <div className="editor-form-input">
            <SwitchOnOff
              checked={params.arrows_on_hover}
              iconName=""
              name="arrows_on_hover"
              size="sm"
              onChange={(value: boolean): void => {
                handleChangeOption("arrows_on_hover", value);
              }}
            />
          </div>
          <div className="editor-form-label">{t("showArrowHover")}</div>
        </div>
        <ComponentDisplayOrder
          data={carouselOrMosaicComponent.params.display_order}
          hasPublicationDateOrder={isSmartlistOrCollectionPage}
          onChange={handleChangeDisplayOrder}
        />
        <div className="editor-form-row m-t-10">
          <div className="editor-form-label">{t("componentPerSlides")}</div>
          <div className="editor-form-input">
            <Slider
              dots
              defaultValue={params.component_per_slides || 6}
              marks={marks}
              max={10}
              min={1}
              onChange={handleChangeComponentPerSlides}
              railStyle={{ backgroundColor: "var(--surface-muted)", height: 8 }}
              step={1}
              activeDotStyle={{
                borderColor: `${editor_primary_color}`,
                bottom: -6,
                width: 12,
                height: 12,
                marginLeft: -6,
              }}
              dotStyle={{
                bottom: -6,
                width: 12,
                height: 12,
                marginLeft: -6,
              }}
              handleStyle={{
                borderColor: `${editor_primary_color} !important`,
                height: 20,
                width: 20,
                marginLeft: 0,
                marginTop: -6,
              }}
              trackStyle={{
                backgroundColor: `${editor_primary_color} !important`,
                height: 8,
              }}
            />
          </div>
        </div>

        {isCarousel &&
        carouselOrMosaicComponent.params.component_per_slides === 1 ? (
          <div>
            <div
              key="switch"
              className="editor-form-row editor-form-row-inline"
            >
              <div className="editor-form-input">
                <SwitchOnOff
                  checked={carouselOrMosaicComponent.params.display_button}
                  name="display_button"
                  onChange={handleChangeDisplayButton}
                  size="sm"
                />
              </div>
              <div className="editor-form-label">{t("showButton")}</div>
            </div>
            {Boolean(carouselOrMosaicComponent.params.display_button) && (
              <div className="editor-form-row editor-form-row-half-size">
                <div className="editor-form-label">{t("buttonPosition")}</div>
                <div className="editor-form-input">
                  <CarouselButtonPosition
                    onPositionChange={handleChangeButtonPosition}
                    ratio={carouselOrMosaicComponent.params.ratio}
                    buttonHorizontalPosition={
                      carouselOrMosaicComponent.params
                        .button_horizontal_position
                    }
                    buttonVerticalPosition={
                      carouselOrMosaicComponent.params.button_vertical_position
                    }
                  />
                </div>
              </div>
            )}
          </div>
        ) : null}
      </React.Fragment>
    );
  };

  const getSwitchClassNames = (
    componentType: typeof CAROUSEL | typeof MOSAIC,
  ): string =>
    classNames("editor-component-type", {
      selected: carouselOrMosaicComponent.type === componentType,
    });

  return (
    <div className="editor-tile-container">
      {children ? <div key="editor-section-children">{children}</div> : null}
      <div
        className={`editor-form-section ${allowWidget ? "section-separator" : ""}`}
      >
        <div className="editor-form-row">
          <div className="editor-form-section-title">{t("componentType")}</div>
          <div className="editor-form-input">
            <div
              className={getSwitchClassNames(CAROUSEL)}
              onClick={(): void => handleClickComponentType(CAROUSEL)}
            >
              <div className="editor-component-type-icon">
                <i className="material-icons">view_carousel</i>
              </div>
              <span>{t("carousel")}</span>
            </div>
            <div
              className={getSwitchClassNames(MOSAIC)}
              onClick={(): void => handleClickComponentType(MOSAIC)}
            >
              <div className="editor-component-type-icon">
                <i className="material-icons">view_module</i>
              </div>
              <span>{t("mosaic")}</span>
            </div>
          </div>
        </div>
        <ImageFormatSelector
          imageFormatList={imageFormatList}
          selected={carouselOrMosaicComponent.params.ratio}
          title={t("pictureRatio")}
          onChangeRatio={(ratio: ImageFormat): void => {
            handleChangeOption("ratio", ratio);
          }}
        />
        {showTitle ? (
          <div className="editor-form-row editor-form-row-inline">
            <div className="editor-form-input">
              <SwitchOnOff
                checked={carouselOrMosaicComponent.params.display_title}
                iconName=""
                name="display_title"
                size="sm"
                onChange={(value: boolean): void => {
                  handleChangeOption("display_title", value);
                }}
              />
            </div>
            <div className="editor-form-label">
              {t(componentLabels.showComponentTitle)}
            </div>
          </div>
        ) : null}
        {isSmartlistOrCollectionPage ? (
          <div className="editor-form-row editor-form-row-inline">
            <div className="editor-form-input">
              <SwitchOnOff
                checked={carouselOrMosaicComponent.params.display_castings}
                iconName=""
                name="display_castings"
                size="sm"
                onChange={(value: boolean): void => {
                  handleChangeOption("display_castings", value);
                }}
              />
            </div>
            <div className="editor-form-label">{t("showCurators")}</div>
          </div>
        ) : null}
        <div className="editor-form-row editor-form-row-inline">
          <div className="editor-form-input">
            <SwitchOnOff
              checked={carouselOrMosaicComponent.params.display_item_title}
              iconName=""
              name="display_item_title"
              size="sm"
              onChange={(value: boolean): void => {
                handleChangeOption("display_item_title", value);
              }}
            />
          </div>
          <div className="editor-form-label">{t("showItemsTitle")}</div>
        </div>
        <div className="editor-form-row editor-form-row-inline">
          <div className="editor-form-input">
            <SwitchOnOff
              checked={carouselOrMosaicComponent.params.display_dialog}
              iconName=""
              name="display_dialog"
              size="sm"
              onChange={(value: boolean): void => {
                handleChangeOption("display_dialog", value);
              }}
            />
          </div>
          <div className="editor-form-label">{t("showDialog")}</div>
        </div>

        {carouselOrMosaicComponent.type === CAROUSEL && (
          <React.Fragment>
            {renderCommonCarouselParams(carouselOrMosaicComponent.params)}
          </React.Fragment>
        )}

        {isSmartlistOrCollectionPage ? (
          <React.Fragment>
            <div
              key="switch-individual-carousel"
              className="editor-form-row editor-form-row-inline"
            >
              <div className="editor-form-input">
                <SwitchOnOff
                  checked={carouselOrMosaicComponent.params.individual_carousel}
                  iconName=""
                  name="individual_carousel"
                  onChange={handleChangeIndividualCarousel}
                  size="sm"
                />
              </div>
              <div className="editor-form-label">
                {t("individualCarousel")}
                <InfoTooltip position="right" type="icon">
                  {carouselOrMosaicComponent.type === CAROUSEL
                    ? t(componentLabels.carouselIndividualInfo)
                    : t(componentLabels.mosaicIndividualInfo)}
                </InfoTooltip>
              </div>
            </div>
            {isMosaicWithIndividualCarousel ? (
              <div className="editor-form-section">
                {renderCommonCarouselParams(
                  carouselOrMosaicComponent.params.opts_individual_carousel,
                )}
              </div>
            ) : null}
          </React.Fragment>
        ) : null}
      </div>
      {allowWidget ? (
        <div className="editor-form-section">
          <div className="editor-form-section-title">
            {t("editor-widget:widget")}
          </div>
          <div className="editor-form-row">
            <div className="editor-form-row editor-form-row-inline">
              <div className="editor-form-input">
                <SwitchOnOff
                  checked={widgetComponent?.enabled}
                  iconName=""
                  name="activate_widget"
                  onChange={handleChangeWidgetComponentEnabled}
                  size="sm"
                />
              </div>
              <div className="editor-form-label">
                {t("editor-widget:activateWidget")}
              </div>
              <InfoTooltip>
                <MarkdownRenderer markdown={t("editor-help:widgetTooltip")} />
              </InfoTooltip>
            </div>
          </div>
          {widgetComponent.enabled ? (
            <WidgetConfig
              component={widgetComponent}
              onChange={handleChangeWidgetComponent}
              description={t(
                `editor-widget:widget_desc_${widgetComponent.section}`,
              )}
            />
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export default CommonTypeEditor;
